import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import UCPageShell from "../../../components/ui/UCPageShell";
import SectionTitle from "../../../components/shared/SectionTitle";
import UCTable from "../../../components/ui/UCTable";
import StressDot from "../../../components/ui/StressDot";
import StatCard from "../../../components/ui/StatCard";
import AceAvatar from "../../../components/shared/AceAvatar";
import { fetchUsersClassesOverview } from "../../../services/usersClassesService";
import {
  canAccessClassRecord,
  getAssignedClasses,
  getCurrentUser,
  isAdminUser,
} from "../../../utils/auth";

function normalizeClass(value) {
  return String(value || "").trim().toLowerCase();
}

function scopeOverview(data) {
  const currentUser = getCurrentUser();

  if (isAdminUser(currentUser)) {
    return data || { users: [], classes: [] };
  }

  const allowed = new Set(
    (getAssignedClasses(currentUser) || []).map(normalizeClass)
  );

  const users = (data?.users || []).filter((user) =>
    allowed.has(normalizeClass(user?.className))
  );

  const classes = (data?.classes || []).filter((klass) =>
    allowed.has(normalizeClass(klass?.name || klass?.className || klass?.id))
  );

  return { users, classes };
}

export default function ClassDetailsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const backTo =
    location.state?.backTo ||
    (location.state?.fromStats ? "/stats" : "/users-classes/classes");

  const backLabel =
    location.state?.backLabel ||
    (location.state?.fromStats
      ? "← Zurück zu Statistiken"
      : "← Zurück zu Klassen");

  const [overview, setOverview] = useState({ users: [], classes: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const data = await fetchUsersClassesOverview();
        if (!mounted) return;
        setOverview(scopeOverview(data));
      } catch (error) {
        console.error("Klassen-Details konnten nicht geladen werden:", error);
        if (!mounted) return;
        setOverview({ users: [], classes: [] });
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, []);

  const klass = useMemo(
    () => overview.classes.find((entry) => entry.id === id) || null,
    [overview.classes, id]
  );

  const hasAccess = useMemo(() => {
  if (!klass) return false;
  return canAccessClassRecord(klass.name || klass.id);
}, [klass]);



  const inClass = useMemo(() => klass?.users || [], [klass]);

  const stats = useMemo(() => {
    const total = inClass.length;
    const motiviert = inClass.filter((u) => Number(u.averageStress || 0) <= 2).length;
    const ruhig = inClass.filter((u) => Number(u.averageStress || 0) === 3).length;
    const elevated = inClass.filter((u) => Number(u.averageStress || 0) >= 4).length;

    return { total, motiviert, ruhig, elevated };
  }, [inClass]);

  const aceVariant = useMemo(() => {
    if (stats.elevated >= 2) return "stress2";
    if (stats.elevated === 1) return "ruhig1";
    return "motiviert1";
  }, [stats]);

  const classNote = useMemo(() => {
    if (stats.elevated >= 2) {
      return "In dieser Klasse gibt es mehrere auffällige Belastungswerte. Ein genauer Blick auf einzelne Profile wäre sinnvoll.";
    }
    if (stats.elevated === 1) {
      return "Es gibt einen leicht auffälligen Fall. Insgesamt wirkt die Klasse aber noch stabil.";
    }
    return "Die Klasse wirkt derzeit stabil und unauffällig.";
  }, [stats]);

  const cols = useMemo(
    () => [
      {
        key: "name",
        label: "Name",
        render: (u) => (
          <div className="font-semibold text-ac-text">
            {u.displayName}
            <div className="text-xs text-ac-muted">{u.email || "Keine E-Mail gespeichert"}</div>
          </div>
        ),
      },
      {
        key: "stress",
        label: "Status",
        render: (u) => <StressDot stress={u.averageStress} />,
      },
      { key: "lastActiveLabel", label: "Zuletzt aktiv" },
      {
        key: "action",
        label: "Aktion",
        render: (u) => (
          <button
            type="button"
            onClick={() =>
              navigate(`/users-classes/users/${u.id}`, {
                state: {
                  ...location.state,
                  backTo,
                  backLabel,
                },
              })
            }
            className="ac-ghostbtn ac-uc-tablebtn"
          >
            User öffnen
          </button>
        ),
      },
    ],
    [navigate, location.state, backTo, backLabel]
  );

  if (loading) {
    return (
      <UCPageShell title="Klassen-Details">
        <div className="ac-soft-panel ac-uc-block">
          <div className="text-sm text-ac-text">Lade Klassen-Details…</div>
        </div>
      </UCPageShell>
    );
  }

  if (!klass || !hasAccess) {
    return (
      <UCPageShell title="Klassen-Details">
        <div className="ac-soft-panel ac-uc-block">
          <div className="text-sm text-ac-text">Klasse nicht gefunden.</div>
          <button
            type="button"
            onClick={() => navigate(backTo)}
            className="mt-4 ac-primarybtn"
          >
            {backLabel}
          </button>
        </div>
      </UCPageShell>
    );
  }

  return (
    <UCPageShell
      title="Klassen-Details"
      right={
        <button
          type="button"
          onClick={() => navigate(backTo)}
          className="ac-ghostbtn"
        >
          {backLabel}
        </button>
      }
    >
      <div className="space-y-5">
        <section className="ac-soft-panel ac-uc-hero">
          <div className="ac-uc-hero-grid">
            <div>
              <h2 className="ac-uc-hero-title">{klass.name}</h2>
              <p className="ac-uc-hero-text">
                {klass.school}. Hier siehst du die Klassenverteilung, den aktuellen
                Status und alle zugehörigen Profile im Überblick.
              </p>
            </div>

            <div className="ac-uc-hero-side">
              <div className="ac-uc-acebox">
                <AceAvatar variant={aceVariant} size={56} />
                <div className="ac-uc-acebubble">
                  Ace sagt: In der Klassenansicht lassen sich Entwicklungen oft
                  schneller erkennen.
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="ac-soft-panel ac-uc-block">
          <SectionTitle
            title="Klassenstatus"
            subtitle="Zusammenfassung der aktuellen Verteilung innerhalb der Klasse"
          />

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <StatCard
              title="Insgesamt"
              value={stats.total}
              hint="Schüler:innen"
              iconText="👥"
            />
            <StatCard
              title="Motiviert"
              value={stats.motiviert}
              hint="sehr stabil"
              iconText="🩵"
            />
            <StatCard
              title="Ruhig"
              value={stats.ruhig}
              hint="ausgeglichen"
              iconText="💚"
            />
            <StatCard
              title="Belastet"
              value={stats.elevated}
              hint="beobachten"
              iconText="🧡"
            />
          </div>
        </section>

        <section className="ac-soft-panel ac-uc-block">
          <div className="ac-uc-detail-callout">
            <div>
              <div className="ac-uc-detail-callout-title">Hinweis zur Klasse</div>
              <div className="ac-uc-detail-callout-text">{classNote}</div>
            </div>
          </div>
        </section>


        <section className="ac-soft-panel ac-uc-block">
          <div className="ac-uc-detail-callout">
            <div>
              <div className="ac-uc-detail-callout-title">Lehrkraft-Zugang</div>
              <div className="ac-uc-detail-callout-text">
                {klass.teacherAssigned
                  ? `${klass.teacherNames} · ${klass.teacherInfo}`
                  : "Für diese Klasse ist aktuell noch kein Lehrkraft-Zugang freigegeben."}
              </div>
            </div>

            <button
              type="button"
              onClick={() =>
                navigate("/users-classes/users", {
                  state: {
                    ...location.state,
                    backTo,
                    backLabel,
                  },
                })
              }
              className="ac-primarybtn ac-uc-smallbtn"
            >
              Alle User ansehen
            </button>
          </div>
        </section>


        <section className="ac-soft-panel ac-uc-block">
          <SectionTitle
            title="Schüler:innen dieser Klasse"
            subtitle='Klick auf „User öffnen“ für Details.'
          />
          <div className="mt-4">
            <UCTable columns={cols} rows={inClass} rowKey={(u) => u.id} />
          </div>
        </section>
      </div>
    </UCPageShell>
  );
}